<template>
  <div class="kt-portlet kt-portlet--mobile">
    <div class="kt-portlet__head kt-portlet__head--lg">
      <div class="kt-portlet__head-label">
        <span class="kt-portlet__head-icon">
          <i class="fas fa-file-alt"></i>
        </span>
        <h3 class="kt-portlet__head-title">
          ตรวจสอบข้อมูลก่อนสร้างเอกสารรวม
        </h3>
      </div>
    </div>
    <div class="kt-portlet__body">
      <!-- begin: Stepper -->
      <div class="row">
        <div class="col-12">
          <div class="fss-stepper">
            <div class="fss-step fss-step--done">
              <div class="fss-step__badge">&#10003;</div>
              <div class="fss-step__title">เลือก Filter</div>
            </div>
            <div class="fss-connector fss-connector--done"></div>
            <div class="fss-step fss-step--active">
              <div class="fss-step__badge">2</div>
              <div class="fss-step__title">ตรวจสอบข้อมูล</div>
            </div>
            <div class="fss-connector"></div>
            <div class="fss-step">
              <div class="fss-step__badge">3</div>
              <div class="fss-step__title">บันทึก + สร้างเอกสาร</div>
            </div>
          </div>
        </div>
      </div>
      <!-- end: Stepper -->

      <!-- begin: Warning banner -->
      <div class="row">
        <div class="col-12">
          <div class="alert alert-warning" role="alert">
            <strong>ยังไม่บันทึก</strong> —
            ตรวจสอบข้อมูลด้านล่างให้ครบถ้วน จากนั้นกด "บันทึกข้อมูล"
            เพื่อสร้างเอกสารทีละฉบับให้ตัวแทนจำหน่ายที่มีข้อมูล
          </div>
        </div>
      </div>
      <!-- end: Warning banner -->

      <!-- begin: QA 08/10/2026 เรื่องที่ 1 — บรรทัดสาขาของสำนักงานใหญ่ที่เลือก
           หน้าตัวแทนรายเดียว (Screen 3) มีบรรทัดนี้อยู่แล้ว แต่หน้าเอกสารรวม (3B) ไม่มี
           ผู้ใช้จึงไม่รู้ว่ายอดรวมนี้มาจากสาขาไหนบ้าง
           ใช้ endpoint CustomerBox ตัวเดียวกับที่ฟอร์ม PDF ใช้ ข้อมูลจึงตรงกันเสมอ -->
      <div v-if="branchLine" class="row">
        <div class="col-12">
          <div
            class="fss-branch-line"
            data-test="fss-group-branch-line"
          >
            {{ branchLine }}
          </div>
        </div>
      </div>
      <!-- end: เรื่องที่ 1 -->

      <!-- begin: Filter ที่เลือก -->
      <div class="row">
        <div class="col-12">
          <h5 class="kt-font-bold kt-margin-b-10">Filter ที่เลือก</h5>
          <div v-if="filterText" class="fss-filter-text">
            {{ filterText }}
          </div>
          <table v-else-if="filterLabels.length > 0" class="table table-sm fss-filter-table">
            <tbody>
              <tr v-for="(item, index) in filterLabels" :key="index">
                <td class="fss-filter-table__label">{{ item.label }}</td>
                <td>{{ item.text }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-muted">-</div>
        </div>
      </div>
      <!-- end: Filter ที่เลือก -->

      <!-- begin: Export Excel ตรวจรายละเอียด -->
      <div class="row">
        <div class="col-12">
          <!--
            FS-068 — ผู้ใช้สั่ง 12/10/2026
              1. ตัดหัวข้อ "ต้องการตรวจสอบระดับลึก?" ออก
                 ⚠ mockup หน้า 3B ใน spec ฉบับเซ็นมีหัวข้อนี้ — ออกจาก mockup ตามคำสั่ง ต้องแจ้ง BA
              2. คำเตือนไฟล์ชั่วคราวย้ายไปอยู่ "ใต้ปุ่ม" และชิดขวาตามปุ่ม
                 ลำดับปุ่ม → คำเตือน ตรงกับ mockup อยู่แล้ว (ของเดิมวางคำเตือนไว้ฝั่งซ้ายคนละข้างกับปุ่ม)
          -->
          <div class="fss-export-box">
            <button
              type="button"
              class="btn btn-sm btn-success"
              :disabled="isNoData || isExporting"
              data-test="fss-group-export"
              @click="exportExcel"
            >
              <i class="la la-file-excel-o"></i>
              <span class="kt-hidden-mobile">Export Excel ตรวจรายละเอียด</span>
            </button>
            <div class="fss-export-box__note text-muted" data-test="fss-group-export-note">
              ไฟล์นี้เป็น <strong>ไฟล์ชั่วคราวสำหรับตรวจ</strong> —
              ระบบไม่บันทึกไว้ในระบบ
              (หลังกดบันทึกข้อมูล เอกสารแต่ละฉบับจะมี Excel ของตัวเอง)
            </div>
          </div>
        </div>
      </div>
      <!-- end: Export Excel ตรวจรายละเอียด -->

      <div class="kt-separator kt-separator--border-dashed kt-separator--space-sm"></div>

      <!-- begin: ไม่พบข้อมูล (ทั้งชุด) -->
      <!--
        ครอบ 2 กรณี: filter ไม่ผ่าน (status จาก API) และ "ทุกตัวแทนที่เลือกไม่มีข้อมูลคงค้างเลย"
        ทั้งคู่กดบันทึกไม่ได้ เพราะจะไม่มีเอกสารให้สร้างแม้แต่ฉบับเดียว
      -->
      <div v-if="isNoData" class="row">
        <div class="col-12">
          <div class="alert alert-secondary text-center" role="alert">
            <div class="kt-font-bold">ไม่พบข้อมูล</div>
            <div class="text-muted">{{ noDataMessage }}</div>
          </div>
        </div>
      </div>
      <!-- end: ไม่พบข้อมูล (ทั้งชุด) -->

      <!--
        FS-055 — ช่องค้นหาตัวแทนจำหน่ายเหนือตาราง
          mockup ของ spec มีช่องนี้ทั้งหน้านี้และหน้าสรุปผล (ขั้นที่ 6) แต่เราทำแค่หน้าเดียว
          หน้านี้ใช้ได้ถึง 60 ตัวแทน ไม่มีช่องค้นหาแล้วหาแถวที่ต้องการไม่เจอ
          กรองฝั่ง client ล้วน — ข้อมูลทั้งชุดโหลดมาครบตั้งแต่กดประมวลผลแล้ว
          FS-053 ข้อ 2 — กว้างเต็มตาราง ไม่ใช่กล่องแคบชิดซ้าย
      -->
      <div v-if="!isNoData" class="row">
        <div class="col-lg-12 fss-preview__search">
          <div class="input-group input-group-sm">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="la la-search"></i></span>
            </div>
            <input
              type="text"
              class="form-control"
              data-test="fss-preview-search"
              v-model="agentKeyword"
              placeholder="ค้นหารหัส/ชื่อตัวแทนจำหน่าย..."
            />
            <div class="input-group-append" v-if="agentKeyword">
              <button
                type="button"
                class="btn btn-secondary"
                data-test="fss-preview-search-clear"
                title="ล้างคำค้น"
                @click="agentKeyword = ''"
              >
                <i class="la la-close"></i>
              </button>
            </div>
          </div>
          <div
            v-if="agentKeyword"
            class="fss-preview__search-count"
            data-test="fss-preview-search-count"
          >
            แสดง {{ agentsWithData.length + agentsWithoutData.length + failedAgents.length }} จาก
            {{ agentCount }} รายการ
          </div>
        </div>
      </div>

      <!-- begin: ตารางระดับตัวแทนจำหน่าย -->
      <!-- FS-055 — เปลี่ยนจาก v-else เป็นเงื่อนไขตรง เพราะมีช่องค้นหาคั่นอยู่ข้างบนแล้ว
           (v-else ต้องติดกับ v-if เท่านั้น ไม่งั้นเทมเพลตคอมไพล์ไม่ผ่าน)
           FS-105 — ทุกรายไม่มีข้อมูลแต่มีรายที่ติดขัด ก็ยังต้องเห็นรายชื่อรายที่ติดขัด -->
      <div v-if="!isNoData || allFailedAgents.length > 0" class="row">
        <div class="col-lg-12 table-responsive">
          <table class="table table-sm table-bordered table-hover">
            <thead>
              <tr>
                <th class="text-center" style="width: 5%;"></th>
                <th class="text-center" style="width: 8%;">ลำดับ</th>
                <th class="text-center">ชื่อตัวแทนจำหน่าย</th>
                <th class="text-center" style="width: 16%;">จำนวนคงค้าง</th>
                <th class="text-center" style="width: 22%;">ยอดรวม (ก่อน VAT)</th>
              </tr>
            </thead>
            <tbody>
              <!-- ── ตัวแทนที่มีข้อมูล — เรียงจากมูลค่ามากไปน้อย (API เรียงมาแล้ว) ── -->
              <template v-for="agent in agentsWithData">
                <!-- FS-055 — ใส่ data-test ให้เทสจับแถวได้อย่างมั่นคง
                     (แถวรายละเอียดที่กางออกมาจะไม่ติด selector นี้) -->
                <tr :key="agent.agentCode" data-test="fss-group-agent-row">
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-clean btn-icon btn-icon-md"
                      :title="isExpanded(agent.agentCode) ? 'ซ่อนรายละเอียด' : 'แสดงรายละเอียด'"
                      @click="toggleDetail(agent.agentCode)"
                    >
                      <i
                        :class="
                          isExpanded(agent.agentCode)
                            ? 'la la-angle-down'
                            : 'la la-angle-right'
                        "
                      ></i>
                    </button>
                  </td>
                  <td class="text-center">{{ agent.seqNo }}</td>
                  <td>
                    {{ agentLabel(agent) }}
                    <!-- FS-106 — สาขาย่อยของแต่ละ Group ใต้ชื่อ (รูปแบบเดียวกับตอนเลือก Group 1 ราย / ฟอร์ม PDF)
                         โหมดตัวแทนจำหน่าย (AGENT) ไม่มีค่านี้ จึงไม่แสดง — FS-107 -->
                    <div v-if="agent.branchLine" class="fss-branch-line" data-test="fss-group-agent-branch-line">
                      {{ agent.branchLine }}
                    </div>
                  </td>
                  <td class="text-right">{{ formatQty(agent.totalQty) }}</td>
                  <td class="text-right">{{ formatAmount(agent.totalAmount) }}</td>
                </tr>

                <!-- แถวรายละเอียดระดับสินค้า — ค่าเริ่มต้นคือพับไว้ -->
                <tr
                  v-if="isExpanded(agent.agentCode)"
                  :key="agent.agentCode + '__detail'"
                  class="fss-detail-row"
                >
                  <td colspan="5">
                    <div class="fss-detail">
                      <div class="fss-detail__title">
                        รายละเอียดสินค้าคงค้าง — {{ agentNameOnly(agent) }}
                      </div>
                      <div class="table-responsive">
                        <table
                          class="table table-sm table-striped table-bordered"
                        >
                          <thead>
                            <tr>
                              <th class="text-center" style="width: 7%;">ลำดับ</th>
                              <th class="text-center" style="width: 18%;">ประเภทสินค้า</th>
                              <th class="text-center" style="width: 15%;">รหัสสินค้า</th>
                              <th class="text-center" style="width: 27%;">ชื่อสินค้า</th>
                              <th class="text-center" style="width: 12%;">จำนวนคงค้าง</th>
                              <th class="text-center" style="width: 8%;">หน่วย</th>
                              <th class="text-center" style="width: 13%;">
                                จำนวนเงิน (ก่อน VAT)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(row, index) in agent.details"
                              :key="index"
                            >
                              <td class="text-center">{{ row.seqNo }}</td>
                              <td>{{ row.productTypeName }}</td>
                              <td>{{ row.productCode }}</td>
                              <td>{{ row.productName }}</td>
                              <td class="text-right">{{ formatQty(row.qty) }}</td>
                              <td class="text-center">{{ row.unitName }}</td>
                              <td class="text-right">{{ formatAmount(row.amount) }}</td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr class="fss-total-row">
                              <td colspan="4" class="text-right kt-font-bold">
                                Grand Total
                              </td>
                              <td class="text-right kt-font-bold">
                                {{ formatQty(agent.totalQty) }}
                              </td>
                              <td class="text-center">—</td>
                              <td class="text-right kt-font-bold">
                                {{ formatAmount(agent.totalAmount) }}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>

              <!-- ── เส้นคั่น + ตัวแทนที่ไม่มีข้อมูล (ระบบจะข้ามไม่สร้างเอกสาร) ── -->
              <tr v-if="agentsWithoutData.length > 0" class="fss-skip-sep">
                <td colspan="5" class="text-center">
                  — ตัวแทนจำหน่ายที่ไม่มีข้อมูลคงค้าง
                  ({{ agentsWithoutData.length }} ราย · ระบบจะข้ามไม่สร้างเอกสาร) —
                </td>
              </tr>
              <tr
                v-for="agent in agentsWithoutData"
                :key="agent.agentCode"
                class="fss-skip-row"
              >
                <td></td>
                <td class="text-center">{{ agent.seqNo }}</td>
                <td>
                  {{ agentLabel(agent) }}
                  <!-- FS-106 — สาขาย่อยของแต่ละ Group ใต้ชื่อ (รูปแบบเดียวกับตอนเลือก Group 1 ราย / ฟอร์ม PDF)
                       โหมดตัวแทนจำหน่าย (AGENT) ไม่มีค่านี้ จึงไม่แสดง — FS-107 -->
                  <div v-if="agent.branchLine" class="fss-branch-line" data-test="fss-group-agent-branch-line">
                    {{ agent.branchLine }}
                  </div>
                </td>
                <td class="text-center">—</td>
                <td class="text-center">—</td>
              </tr>

              <!-- ── FS-105 — ตัวแทนที่ติดขัด เรียกข้อมูลไม่ได้ (ผู้ใช้กด "ข้าม" บน Popup Error) ──
                   ต่อท้ายรายที่สำเร็จ คนละ section · หน้าตาเดียวกับ "ไม่มีข้อมูลคงค้าง" ต่างกันแค่ข้อความ
                   รายเหล่านี้ไม่ถูกส่งไปบันทึก/Export — ผู้ใช้ต้องไปสร้างเอกสารเองภายหลัง -->
              <tr
                v-if="failedAgents.length > 0"
                class="fss-skip-sep fss-failed-sep"
                data-test="fss-group-failed-sep"
              >
                <td colspan="5" class="text-center">
                  — ตัวแทนจำหน่ายที่ติดขัด ไม่สามารถเรียกข้อมูลได้
                  ({{ failedAgents.length }} ราย · ไม่ถูกนำไปสร้างเอกสารในรอบนี้
                  ต้องสร้างเอกสารของรายนี้ภายหลัง) —
                </td>
              </tr>
              <tr
                v-for="agent in failedAgents"
                :key="'failed-' + agent.agentCode"
                class="fss-skip-row fss-failed-row"
                data-test="fss-group-failed-row"
              >
                <td></td>
                <td class="text-center">{{ agent.seqNo }}</td>
                <td>{{ agentLabel(agent) }}</td>
                <td colspan="2" class="text-center fss-failed-note">
                  {{ agent.message || "ติดขัด ไม่สามารถเรียกข้อมูลได้" }}
                  <span v-if="agent.errorReference" class="fss-failed-ref">
                    (เลขอ้างอิง {{ agent.errorReference }})
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="fss-total-row">
                <td colspan="3" class="text-right kt-font-bold">
                  Grand Total (จะสร้างเอกสาร {{ documentCount }} เอกสาร
                  จากทั้งหมด {{ agentCount }} ตัวแทนจำหน่าย)
                </td>
                <td class="text-right kt-font-bold">{{ formatQty(totalQty) }}</td>
                <td class="text-right kt-font-bold">
                  {{ formatAmount(totalAmount) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <!-- end: ตารางระดับตัวแทนจำหน่าย -->

      <div class="kt-separator kt-separator--border-dashed kt-separator--space-sm"></div>

      <!-- begin: Actions -->
      <div class="row">
        <!--
          FS-119 — คอลัมน์ซ้ายกว้างเท่าปุ่ม "แก้ไข Filter" · คอลัมน์ขวาได้ที่ที่เหลือทั้งหมด
            เดิม col-xl-6 / col-xl-6 : viewport 1400–1650px (เช่นจอ 1280 ที่ซูม 80/90%) ปุ่มขวาได้แค่ครึ่งจอ
            ไม่พอสำหรับปุ่มลายเซ็น + ปุ่มบันทึก → ปุ่มหดจนข้อความในปุ่มตกบรรทัด
        -->
        <div class="col-xl-auto">
          <button
            type="button"
            class="btn btn-sm btn-clean kt-margin-r-10"
            @click="goBackToFilter"
          >
            <i class="la la-arrow-left"></i>
            <span class="kt-hidden-mobile">แก้ไข Filter</span>
          </button>
        </div>
        <div class="col-xl text-right">
          <!--
            F15 — เลือกลายเซ็นก่อนสร้างชุด · ภาพที่เลือกจะถูกใช้กับ "ทุกฉบับ" ในชุดนี้
            ยังไม่มีเลขที่เอกสารในขั้นนี้ จึงเปิด popup ในโหมดก่อนบันทึก (id = 0)
          -->
          <!--
            FS-012 — ปุ่มต้องบอกสถานะในตัวเอง
            แนบแล้ว = เขียว + เครื่องหมายถูก (แพตเทิร์นเดียวกับปุ่มแนบไฟล์ในหน้ารายการ)
            ยังไม่แนบ = โทนจางเหมือนเดิม จะได้ไม่เข้าใจผิดว่าแนบไปแล้ว
          -->
          <!--
            FS-018 — spec (CR-Features.md:88 · matrix ข้อ 8) ระบุว่ากลุ่ม B (แอดมินตัวแทน)
            "ไม่แสดงปุ่ม" ระบบจะใช้ลายเซ็นค่าเริ่มต้นให้อัตโนมัติ
            ตัดสินด้วย canManage ที่ API ส่งมา ไม่เดาจาก role ฝั่ง client
            ค่าเริ่มต้นเป็น false = ซ่อนไว้ก่อน กันปุ่มแวบขึ้นมาแล้วหายตอน API ตอบ
          -->
          <!--
            FS-061 — thumbnail และป้าย "ใช้กับทั้ง N เอกสาร" อยู่ "ในปุ่ม" ตาม markup ของ mockup
              <button style="display:flex; align-items:center; gap:8px"
                      title="ลายเซ็นที่จะใช้กับทั้ง N เอกสารในชุดนี้ — คลิกเพื่อเปลี่ยน ...">
                ✅ ลายเซ็นผู้จัดทำ (คลิกเพื่อเปลี่ยน)
                <span style="background:#fff; border:1px solid #145a34">Sig</span>
                <span>ใช้กับทั้ง 55 เอกสาร</span>
              </button>
            เรียงในปุ่ม: ข้อความ → thumbnail → ป้ายจำนวนเอกสาร
            ของเดิมแยกเป็น 3 element นอกปุ่ม ทำให้ตัดบรรทัดแยกกันได้เวลาจอแคบ

            FS-036 ข้อ 7 — ป้ายบอกขอบเขตยังต้องมีเหมือนเดิม เพราะหน้านี้สร้างเอกสารทีเดียว
              หลายฉบับ ลายเซ็นที่เลือกใช้กับ "ทุกฉบับ" ในชุด ถ้าไม่บอกผู้ใช้จะเข้าใจว่า
              เลือกให้ใบเดียวเหมือนหน้าเอกสารเดี่ยว — เปลี่ยนแค่ตำแหน่ง ไม่ได้ตัดทิ้ง
          -->
          <!--
            FS-067 — ปุ่มลายเซ็นกับปุ่มบันทึกต้องอยู่บรรทัดเดียวกันเสมอ
              ของเดิมเป็น inline element เรียงกันเฉย ๆ พอความกว้างรวมเกินคอลัมน์ (50%)
              ปุ่มหลังจะตกบรรทัด ซึ่งเกิดจริงที่ซูม 80% และ 90% แต่ไม่เกิดที่ 70% / 100%
              (ปุ่มลายเซ็นกว้างกว่าปกติเพราะมี thumbnail + ป้าย scope อยู่ในตัว — FS-060/FS-061)
              ครอบด้วย inline-flex + nowrap จึงตัดบรรทัดไม่ได้อีก · ระยะห่างยังใช้ margin เดิม
          -->
          <div class="fss-action-group">
          <button
            v-if="canManageSignature"
            type="button"
            class="btn btn-sm kt-margin-r-10 fss-signature-btn"
            :class="hasSignature ? 'btn-success' : 'btn-clean'"
            :title="
              'ลายเซ็นที่จะใช้กับทั้ง ' + documentCount +
              ' เอกสารในชุดนี้ — คลิกเพื่อเปลี่ยน (เฉพาะแอดมินทีมเบียร์สด)'
            "
            data-test="fss-group-open-signature"
            @click="openSignature"
          >
            <i :class="hasSignature ? 'la la-check' : 'la la-pencil'"></i>
            <span class="kt-hidden-mobile">{{ signatureButtonText }}</span>
            <img
              v-if="signatureImageUrl"
              :src="signatureImageUrl"
              class="fss-signature-thumb"
              alt="ลายเซ็นผู้จัดทำ"
              data-test="fss-group-signature-thumb"
            />
            <span
              class="fss-signature-scope"
              data-test="fss-group-signature-scope"
            >
              ใช้กับทั้ง {{ documentCount }} เอกสาร
            </span>
          </button>

          <button
            type="button"
            class="btn btn-sm btn-primary kt-margin-r-10"
            :disabled="isNoData"
            data-test="fss-group-save"
            @click="saveDocuments"
          >
            <i class="la la-save"></i>
            <span class="kt-hidden-mobile">{{ saveButtonText }}</span>
          </button>
          </div>
        </div>
      </div>
      <!-- end: Actions -->
    </div>
  
    <!-- F14 — popup ความคืบหน้า + ปุ่มยกเลิก (ใช้ร่วมกันได้กับงานยาวอื่น ๆ) -->
    <FormSummaryStockProgressModal
      ref="progressModal"
      @finished="onBatchFinished"
      @closed="onBatchClosed"
    />

    <!-- F15 — popup ลายเซ็น 2 แถบ (ตัวเดียวกับหน้าเอกสารเดี่ยว) -->
    <!--
      FS-069 — @changed มาทุกครั้งที่บันทึกภาพสำเร็จ (ทั้งสองแถบ)
        ของเดิมไม่มีใครฟัง → บันทึกค่าเริ่มต้นระบบในแถบ 2 แล้ว thumbnail บนปุ่มค้างภาพเก่า
        loadDefaultSignature() มีด่าน "ถ้ามีภาพเฉพาะกิจอยู่แล้ว ไม่ทับ" อยู่ในตัว
        จึงเรียกได้เลย — ภาพเฉพาะกิจจากแถบ 1 ยังชนะค่าเริ่มต้นเสมอ
    -->
    <FormSummaryStockSignatureModal
      ref="signatureModal"
      @selected="onSignatureSelected"
      @changed="loadDefaultSignature"
    />
</div>
</template>

<script>
import axios from "axios";
// Action Button Design System + Stepper ของโมดูลนี้ (QA เรื่องที่ 11 + 16)
import "./form-summary-stock-actions.css";
// FS-088 — tooltip ของปุ่มวงกลมเป็นแบบ Bootstrap (ผูกครั้งเดียวทั้งโมดูล เรียกซ้ำได้)
import { installFssTooltips } from "./form-summary-stock-tooltips";
installFssTooltips();
import router from "../../router";
import FormSummaryStockProgressModal from "./FormSummaryStockProgressModal";
import FormSummaryStockSignatureModal from "./FormSummaryStockSignatureModal";

require("sweetalert2");

// ─────────────────────────────────────────────────────────────────────────────
// CR-FormSummaryStock F12 — ขั้นที่ 3B "ตรวจสอบข้อมูลก่อนสร้างเอกสารรวม"
// (/PrintSummary/Barcodes/New/GroupPreview — ใช้เมื่อเลือกตัวแทนจำหน่าย 2 รายขึ้นไป)
//
// หน้านี้ยังไม่บันทึกอะไรลงฐานข้อมูล — แสดงผลลัพธ์ที่ FormSummaryStockCreate.vue
// ยิง /Api/FormSummaryStock/GroupProcess มาแล้ว เพื่อให้ผู้ใช้ตรวจก่อนกด "บันทึกข้อมูล"
//
// การรับ state จากขั้นที่ 1: อ่านจาก $parent (= FormSummaryStockCreate.vue ซึ่งเป็น route แม่)
//   กลไกเดียวกับ FormSummaryStockPreview.vue:213-228 เป๊ะ ๆ
//   (repo นี้ไม่มี Vuex / store module / การส่ง object ผ่าน route params — $parent คือ pattern เดียวที่มี)
//   → route ของหน้านี้ต้องเป็น "ลูกของ New" เหมือน Preview ไม่งั้น $parent จะกลายเป็นหน้ารายการ
//
// ⚠ ต้องให้ FormSummaryStockCreate.vue เพิ่ม state 2 ตัว (ยังไม่มีในไฟล์นั้น — เป็นงาน D9/F03):
//     groupProcessResult : ผลจาก /Api/FormSummaryStock/GroupProcess
//     groupAgentCodes    : array ของรหัสตัวแทนที่ผู้ใช้เลือก (ใช้ยิง GroupExportExcel ต่อ)
//   ระหว่างที่ยังไม่มี หน้านี้ถอยไปอ่าน processResult / filter.agentCodes ให้เอง (ดู computed ด้านล่าง)
//
// เข้าหน้านี้ตรง ๆ (refresh / กด URL) จะไม่มีผลประมวลผล → เด้งกลับขั้นที่ 1
//
// SweetAlert2 = ^8.2.3 (SBP.Web/package.json) → ใช้ option ชื่อ `type` (ไม่ใช่ `icon` ของ v9+)
// และอ่านผลจาก `result.value`
// ─────────────────────────────────────────────────────────────────────────────

// ปุ่มที่ยังไม่ถึงคิวพัฒนา — แจ้งผู้ใช้เป็นภาษาไทยแทนการเงียบ (ห้ามใช้ alert()/console.log)
// รูปแบบเดียวกับ FormSummaryStockDetail.vue:250 และ FormSummaryStockList.vue
function showUnderDevelopment(featureName) {
  Swal.fire("อยู่ระหว่างพัฒนา", "ฟังก์ชัน " + featureName + " อยู่ระหว่างพัฒนา", "info");
}

// สำเนา filter แบบตื้น + แนบรหัสตัวแทนที่เลือก (หลีกเลี่ยง Object.assign ให้ตรงกับ ES5 ที่ repo ใช้อยู่)
function withAgentCodes(filter, agentCodes) {
  var payload = {};
  for (var key in filter) {
    if (Object.prototype.hasOwnProperty.call(filter, key)) {
      payload[key] = filter[key];
    }
  }
  payload.agentCodes = agentCodes;
  return payload;
}

export default {
  components: { FormSummaryStockProgressModal, FormSummaryStockSignatureModal },
  data: function() {
    return {
      // สถานะกาง/พับของแต่ละตัวแทน — key = agentCode
      // ค่าเริ่มต้นคือ "พับไว้ทั้งหมด" (spec: ค่าเริ่มต้น = ซ่อน)
      expandedAgents: {},
      // FS-055 — คำค้นของช่องค้นหาตัวแทนเหนือตาราง (กรองฝั่ง client)
      agentKeyword: "",
      isExporting: false,
      // F11 — กันกดซ้ำระหว่างสร้างเอกสารรวม (งานยาว อาจหลายนาที)
      isSaving: false,
      // F15 — ภาพลายเซ็นที่เลือกไว้ จะถูกใช้กับทุกฉบับในชุด (null = ใช้ค่าเริ่มต้นระบบ)
      signatureAttachedId: null,
      // FS-017 — ดู FormSummaryStockPreview.vue (กติกาเดียวกัน ภาพนี้ใช้กับทุกฉบับในชุด)
      hasDefaultSignature: false,
      signatureImageUrl: "",
      // QA เรื่องที่ 1 — "*สำนักงานใหญ่ + สาขา [ ... ]" (ว่าง = ไม่ต้องแสดง)
      branchLine: "",
      // FS-018 — สิทธิ์จัดการลายเซ็น (canManage จาก /Api/FormSummaryStock/Signature)
      //   ค่าเริ่มต้นต้องเป็น false เสมอ — ซ่อนปุ่มไว้ก่อนจนกว่า API จะยืนยันว่ามีสิทธิ์
      canManageSignature: false,
    };
  },
  computed: {
    // FS-012 — สถานะปุ่มลายเซ็น
    //   ขั้นนี้ยังไม่บันทึกเอกสาร "แนบแล้ว" จึงหมายถึงผู้ใช้เลือกภาพเฉพาะกิจไว้แล้ว
    //   (null = ปล่อยให้ทุกฉบับในชุดใช้ลายเซ็นค่าเริ่มต้นของระบบ)
    // FS-021 — ตกหล่นจาก FS-017 (ตอนนั้นแก้ Preview/Detail ไปแล้ว แต่หน้านี้ได้แค่ครึ่งเดียว)
    //   "มีลายเซ็น" = เอกสารในชุดนี้จะพิมพ์ออกมาพร้อมภาพแน่นอน ไม่ได้เลือกภาพเฉพาะกิจ
    //   ก็ยังได้ลายเซ็นค่าเริ่มต้นของระบบ ซึ่ง spec (CR-Features.md:320) ระบุว่า
    //   "ระบบต้องมีภาพเสมอ" → สถานะเริ่มต้นจึงต้องเป็นเขียว ไม่ใช่โทนจางทั้งที่ thumbnail ขึ้นข้าง ๆ
    hasSignature: function() {
      return !!this.signatureAttachedId || this.hasDefaultSignature;
    },
    signatureButtonText: function() {
      return this.hasSignature
        ? "ลายเซ็นผู้จัดทำ (คลิกเพื่อเปลี่ยน)"
        : "ลายเซ็นผู้จัดทำ";
    },

    // ผลลัพธ์จาก /Api/FormSummaryStock/GroupProcess ที่ขั้นที่ 1 เก็บไว้
    // (ถอยไปอ่าน processResult ได้ ถ้าขั้นที่ 1 ยังใช้ชื่อเดิมอยู่และมี agents ติดมา)
    groupResult: function() {
      var parent = this.$parent;
      if (!parent) return null;
      if (parent.groupProcessResult) return parent.groupProcessResult;
      if (parent.processResult && parent.processResult.agents) return parent.processResult;
      return null;
    },
    filterText: function() {
      return this.groupResult && this.groupResult.filterText
        ? this.groupResult.filterText
        : "";
    },
    filterLabels: function() {
      return this.$parent && this.$parent.filterLabels
        ? this.$parent.filterLabels
        : [];
    },
    agents: function() {
      return this.groupResult && this.groupResult.agents
        ? this.groupResult.agents
        : [];
    },
    // API เรียงมาให้แล้ว (มีข้อมูลก่อน → มูลค่ามากไปน้อย) — ที่นี่แค่แยกเป็น 2 ส่วนเพื่อวาดเส้นคั่น
    /**
     * FS-055 — ตัวแทนที่ตรงกับคำค้น
     *   ค้นได้ทั้งรหัสและชื่อ แบบเดียวกับหน้าสรุปผล (GroupResult)
     *   ตัด case ทิ้งและตัดช่องว่างหัวท้าย กัน paste ติดมา
     */
    matchedAgents: function() {
      const keyword = (this.agentKeyword || "").trim().toLowerCase();
      if (!keyword) return this.agents;

      return this.agents.filter(function(agent) {
        const haystack = (
          (agent.agentCode || "") + " " + (agent.agentName || "")
        ).toLowerCase();
        return haystack.indexOf(keyword) !== -1;
      });
    },
    agentsWithData: function() {
      return this.matchedAgents.filter(function(agent) {
        return agent.hasData;
      });
    },
    agentsWithoutData: function() {
      return this.matchedAgents.filter(function(agent) {
        return !agent.hasData;
      });
    },
    // FS-105 — ตัวแทนที่ติดขัด เรียกข้อมูลไม่ได้ และผู้ใช้กด "ข้าม" (API ส่งแยกจาก agents)
    //   ลำดับไล่ต่อจากตัวแทนในตารางหลัก ให้คอลัมน์ "ลำดับ" ต่อเนื่องทั้งตาราง
    allFailedAgents: function() {
      const list = this.groupResult && this.groupResult.failedAgents
        ? this.groupResult.failedAgents
        : [];
      const offset = this.agents.length;

      return list.map(function(agent, index) {
        return Object.assign({}, agent, { seqNo: offset + index + 1 });
      });
    },
    failedAgents: function() {
      const keyword = (this.agentKeyword || "").trim().toLowerCase();
      if (!keyword) return this.allFailedAgents;

      return this.allFailedAgents.filter(function(agent) {
        const haystack = (
          (agent.agentCode || "") + " " + (agent.agentName || "")
        ).toLowerCase();
        return haystack.indexOf(keyword) !== -1;
      });
    },
    totalQty: function() {
      return this.groupResult ? this.groupResult.totalQty : 0;
    },
    totalAmount: function() {
      return this.groupResult ? this.groupResult.totalAmount : 0;
    },
    // FS-105 — "จากทั้งหมด N ตัวแทนจำหน่าย" ต้องนับรายที่ติดขัดด้วย (ผู้ใช้เลือกมาครบ N ราย)
    agentCount: function() {
      return this.agents.length + this.allFailedAgents.length;
    },
    // จำนวนเอกสารที่จะถูกสร้างจริง — ใช้ค่าจาก API เป็นหลัก (แหล่งความจริงเดียว)
    //
    // FS-055 — ทางถอยต้องนับจาก "ตัวแทนทั้งหมด" ไม่ใช่ agentsWithData ที่ถูกกรองด้วยคำค้น
    //   ไม่งั้นพิมพ์ค้นหาแล้วจำนวนเอกสารที่จะสร้างจะลดตามไปด้วย ซึ่งอันตรายมาก
    //   (ช่องค้นหามีไว้ดูข้อมูลเท่านั้น ห้ามมีผลกับสิ่งที่จะถูกบันทึก)
    documentCount: function() {
      if (this.groupResult && this.groupResult.documentCount != null) {
        return this.groupResult.documentCount;
      }
      return this.agents.filter(function(agent) {
        return agent.hasData;
      }).length;
    },
    saveButtonText: function() {
      return "บันทึกข้อมูล (สร้าง " + this.documentCount + " เอกสาร)";
    },
    // ไม่มีเอกสารให้สร้างเลย → กดบันทึก/Export ไม่ได้
    isNoData: function() {
      return this.documentCount === 0;
    },
    noDataMessage: function() {
      if (this.groupResult && this.groupResult.message) {
        return this.groupResult.message;
      }
      return "ไม่พบรายการสินค้าคงค้างของตัวแทนจำหน่ายที่เลือกเลย กรุณาแก้ไข Filter แล้วประมวลผลใหม่อีกครั้ง";
    },
    // รหัสตัวแทนที่จะส่งไปกับ API — ไล่จากแหล่งที่เชื่อถือได้มากไปน้อย
    agentCodes: function() {
      var parent = this.$parent;

      if (parent && parent.groupAgentCodes && parent.groupAgentCodes.length > 0) {
        return parent.groupAgentCodes.slice();
      }

      var filter = parent ? parent.filter : null;
      if (filter && filter.agentCodes && filter.agentCodes.length > 0) {
        return filter.agentCodes.slice();
      }

      // ถอยไปใช้รหัสที่ API เพิ่งคืนมา — ตรงกับตารางที่ผู้ใช้เห็นอยู่ตรงหน้าเสมอ
      return this.agents.map(function(agent) {
        return agent.agentCode;
      });
    },
  },
  methods: {
    formatQty: function(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("en-US");
    },
    formatAmount: function(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // FS-106 ข้อ 2 / FS-107 — หัวตารางรายละเอียดสินค้าแสดงชื่อตัวแทนอย่างเดียว ไม่มี [รหัส]
    //   (หาชื่อไม่เจอค่อยถอยไปใช้รหัส ไม่ให้หัวตารางว่าง)
    agentNameOnly: function(agent) {
      if (!agent) return "";
      return agent.agentName || agent.agentCode || "";
    },
    agentLabel: function(agent) {
      if (!agent) return "-";
      if (!agent.agentName) return "[" + agent.agentCode + "]";
      return "[" + agent.agentCode + "] " + agent.agentName;
    },
    isExpanded: function(agentCode) {
      return this.expandedAgents[agentCode] === true;
    },
    // Vue 2 ไม่ตรวจจับการเพิ่ม key ใหม่ในอ็อบเจกต์ → ต้องใช้ $set
    toggleDetail: function(agentCode) {
      this.$set(this.expandedAgents, agentCode, !this.isExpanded(agentCode));
    },
    goBackToFilter: function() {
      router.push({ path: "/PrintSummary/Barcodes/New" });
    },

    // ── FS-033 : Export Excel ตรวจรายละเอียด (ไฟล์ชั่วคราว — ระบบไม่เก็บไว้) ──
    //
    //   spec §M นับปุ่มนี้เป็น 1 ใน 5 กรณีที่ต้องมี Popup Progress
    //   ของเดิมยิง request เดียวแล้วบล็อกหน้าจอด้วย KTApp.blockPage — ไม่มี % ไม่มีปุ่มยกเลิก
    //   ทั้งที่งานจริงต้องวนประมวลผลทุกตัวแทนในชุดซ้ำอีกรอบ (หลายนาที)
    //
    //   ตอนนี้ยิง GroupExportExcelStart ที่คืน jobId ทันที แล้วให้ popup poll ความคืบหน้า
    //   ไฟล์พร้อมเมื่อไรผู้ใช้กด "ดาวน์โหลดไฟล์" บน popup (แพตเทิร์นเดียวกับ ZIP ขั้นที่ 6)
    exportExcel: function() {
      var self = this;

      var filter = self.$parent ? self.$parent.filter : null;
      if (!filter) {
        Swal.fire(
          "ไม่พบข้อมูล",
          "ไม่พบเงื่อนไขที่ใช้ประมวลผล กรุณากลับไปเลือก Filter แล้วประมวลผลใหม่อีกครั้ง",
          "warning"
        );
        return;
      }

      if (self.isExporting || self.isSaving) return;
      self.isExporting = true;

      self.$refs.progressModal.start({
        title: "กำลังสร้างไฟล์ Excel ตรวจรายละเอียด",
        jobKind: "file",
        showTimer: true, // FS-126 · FS-139 — ปุ่มนี้ตกหล่นตอนทำ FS-126
        payload: withAgentCodes(filter, self.agentCodes),
        endpoints: {
          start: "/Api/FormSummaryStock/GroupExportExcelStart",
          // ไฟล์ .xlsx ต้องดาวน์โหลดผ่าน endpoint ที่ตอบ content-type ตามนามสกุล
          // (GroupZipDownload ตอบ application/zip ตายตัว ซึ่งใช้กับไฟล์นี้ไม่ได้)
          download: "/Api/FormSummaryStock/GroupFileDownload?jobId={jobId}",
        },
      });
    },

    // F11 (H2) + F14 (K1/K2) — สร้างเอกสารรวมพร้อม popup ความคืบหน้า
    //
    //   งานจริง 55 ตัวแทนกินเวลาหลายนาที เกิน timeout ของ HTTP request ปกติ
    //   จึงใช้ GroupSaveStart ที่คืน batchId ทันที แล้วให้ popup ไป poll ความคืบหน้าเอง
    //   (ไม่ใช้ KTApp.blockPage คั่นเฉย ๆ อีกแล้ว — ผู้ใช้ต้องเห็นว่าถึงไหนและกดยกเลิกได้)
    saveDocuments: function() {
      const self = this;
      const filter = self.$parent ? self.$parent.filter : null;

      if (!filter || !self.agentCodes || self.agentCodes.length === 0) {
        Swal.fire("ไม่พบเงื่อนไข", "กรุณากลับไปเลือกตัวแทนจำหน่ายใหม่อีกครั้ง", "warning");
        return;
      }

      if (self.isSaving) return;

      Swal.fire({
        title: "ยืนยันสร้างเอกสารรวม",
        html:
          "ระบบจะสร้างเอกสาร <strong>1 ฉบับต่อ 1 ตัวแทนจำหน่าย</strong><br>" +
          "ตัวแทนที่ไม่มีสินค้าคงค้างจะถูกข้าม ไม่สร้างเอกสารเปล่า<br><br>" +
          "จำนวนที่จะสร้าง: <strong>" + self.documentCount + "</strong> ฉบับ",
        type: "question",
        showCancelButton: true,
        confirmButtonText: "ยืนยันสร้างเอกสาร",
        cancelButtonText: "ยกเลิก",
      }).then(function(result) {
        // SweetAlert2 v8 — ผลการยืนยันอยู่ที่ result.value
        if (!result.value) return;

        self.isSaving = true;

        self.$refs.progressModal.start({
          title: "กำลังสร้างเอกสารรวม",
          // FS-033 — บอกชนิดงานให้ popup ตรง ๆ ไม่ต้องเดาจาก URL
          //   (งานนี้งานเดียวที่ยกเลิกแล้วลบเอกสาร และมีปุ่ม resume/rollback บน popup error)
          jobKind: "document",
          showTimer: true, // FS-126
          // FS-110 — สร้างครบแล้วค้าง popup ไว้ 1 วินาทีให้เห็น 100% แล้วปิดเอง → ไปขั้นที่ 3 (สรุปผล)
          autoCloseMs: 1000,
          payload: withAgentCodes(filter, self.agentCodes),
          endpoints: { start: self.buildGroupSaveUrl() },
        });
      });
    },

    // ── F15 : ลายเซ็นผู้จัดทำของทั้งชุด ───────────────────────────────────
    openSignature: function() {
      // ยังไม่มีเลขที่เอกสารในขั้นนี้ — ส่ง id = 0 บอก popup ว่าเป็นโหมดก่อนบันทึก
      // (รูปแบบเดียวกับหน้าเอกสารเดี่ยว)
      // FS-070 — ส่งภาพที่หน้านี้ถืออยู่ไปด้วย ไม่งั้นเปิดครั้งที่ 2 popup จะลืมภาพที่เพิ่งบันทึก
      //   (โหมดก่อนบันทึก server ยังไม่มีเอกสารให้ผูกภาพ — หน้านี้คือที่เดียวที่รู้)
      this.$refs.signatureModal.open({
        id: 0,
        documentNo: "",
        attachedId: this.signatureAttachedId,
      });
    },

    // FS-017 — อ่านลายเซ็นค่าเริ่มต้นของระบบตั้งแต่เปิดหน้า (best-effort เหมือนหน้าเอกสารเดี่ยว)
    /**
     * QA 08/10/2026 เรื่องที่ 1 — บรรทัด "*สำนักงานใหญ่ + สาขา [ ... ]"
     *
     * แสดงเฉพาะตอนที่ผู้ใช้เลือก "ตัวแทนจำหน่าย (Group)" มาสำนักงานใหญ่เดียว
     *   เลือกหลายสำนักงานใหญ่ หรือเลือก "ทั้งหมด" จะไม่มีสำนักงานใหญ่ตัวเดียวให้อ้างถึง
     *   การหยิบมาแสดงรายเดียวจะทำให้เข้าใจผิดว่ายอดรวมมาจากกลุ่มนั้นกลุ่มเดียว
     *
     * ใช้ endpoint CustomerBox ตัวเดียวกับหน้าตัวแทนรายเดียวและฟอร์ม PDF
     * (รองรับโหมดก่อนบันทึกอยู่แล้ว — ส่ง headerId = 0)
     */
    loadBranchLine: function() {
      const self = this;
      const filter = self.$parent ? self.$parent.filter : null;

      self.branchLine = "";

      if (!filter) return;

      // รหัสที่เลือกจริง — มาจาก agentCodes ที่ขั้นที่ 1 ประกอบไว้
      const codes = Array.isArray(filter.agentCodes) ? filter.agentCodes : [];
      const headCode = filter.headAgentCode;

      // ไม่ใช่โหมด Group หรือไม่ได้เลือกสำนักงานใหญ่เดียว → ไม่มีบรรทัดนี้
      if (!headCode) return;

      axios
        .post(
          "/Api/FormSummaryStock/CustomerBox",
          {
            headerId: 0,
            filterType: filter.filterType,
            agentCode: null,
            headAgentCode: headCode,
          },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};
          self.branchLine = data.success && data.hasBranchLine ? data.branchLine : "";
        })
        .catch(function() {
          // best-effort — ไม่มีบรรทัดนี้ก็ยังใช้งานหน้าได้ครบ ไม่ต้องรบกวนผู้ใช้
          self.branchLine = "";
        });
    },

    loadDefaultSignature: function() {
      const self = this;

      axios
        .post(
          "/Api/FormSummaryStock/Signature",
          { headerId: 0 },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};

          // FS-018 — handler เซ็ต CanManage ไว้ตั้งแต่บรรทัดแรกของ Handle() จึงเชื่อถือได้
          //   แม้ success = false — อ่านก่อนด่าน success กันกรณี storage ล่มแล้วกลุ่ม A เสียปุ่มไปด้วย
          self.canManageSignature = !!data.canManage;

          if (!data.success) return;

          self.hasDefaultSignature = !data.isMissingDefault && !!data.defaultAttachedId;
          if (!self.signatureAttachedId) {
            self.signatureImageUrl = data.documentImageUrl || data.defaultImageUrl || "";
          }
        })
        .catch(function() {
          self.hasDefaultSignature = false;
          // FS-018 — เรียกไม่สำเร็จ = ไม่รู้สิทธิ์ ให้ถือว่าไม่มีสิทธิ์ (ซ่อนปุ่มไว้ก่อน)
          self.canManageSignature = false;
        });
    },

    onSignatureSelected: function(attachedId, imageUrl) {
      this.signatureAttachedId = attachedId || null;
      // FS-021 — เดิมรับ imageUrl มาแล้วทิ้ง thumbnail จึงค้างเป็นภาพเก่าหลังอัพโหลดใหม่
      //   ล้างภาพเฉพาะกิจแล้ว popup จะส่ง url ของค่าเริ่มต้นกลับมาแทน
      if (typeof imageUrl === "string") this.signatureImageUrl = imageUrl;
    },

    // แนบลายเซ็นเป็น query string — ไม่ปนกับ filter ที่ถูก serialize เก็บเป็น FilterJson
    // ฝั่ง server จะเชื่อค่านี้เฉพาะเมื่อผู้ใช้มีสิทธิ์จัดการลายเซ็นจริง
    buildGroupSaveUrl: function() {
      const base = "/Api/FormSummaryStock/GroupSaveStart";
      return this.signatureAttachedId
        ? base + "?signatureAttachedId=" + encodeURIComponent(this.signatureAttachedId)
        : base;
    },

    // popup รายงานว่างานจบแล้ว (จบเอง / ยกเลิก / ล้มเหลว)
    //
    //   FS-033 — popup ตัวเดียวกันถูกใช้กับงาน Export Excel ด้วยแล้ว
    //   จึงต้องแยกด้วย jobKind ไม่ใช่เหมาเอาว่าทุกงานคือการสร้างเอกสาร
    onBatchFinished: function(summary) {
      var isDocumentJob = !summary || summary.jobKind !== "file";

      if (isDocumentJob) this.isSaving = false;
      else this.isExporting = false;

      // รีเฟรชรายการใบงานที่หน้าค้นหา (route ปู่ = FormSummaryStockList.vue)
      if (
        isDocumentJob &&
        summary &&
        summary.createdCount > 0 &&
        this.$parent &&
        this.$parent.$parent &&
        typeof this.$parent.$parent.reloadDataTable === "function"
      ) {
        this.$parent.$parent.reloadDataTable();
      }
    },

    // ผู้ใช้กดปิด popup แล้ว -> ไปหน้าขั้นที่ 6 สรุปผลทั้งชุด (F13)
    //
    //   ส่ง batchId ทาง query string เพื่อให้หน้านั้น refresh ได้และเปิดซ้ำทีหลังได้
    //   (หน้าขั้นที่ 6 อ่านข้อมูลจากฐานข้อมูล ไม่ใช่จาก state ในหน่วยความจำ)
    //
    //   ไม่มีเอกสารถูกสร้างเลย (ถูกข้ามหมด) ก็ไม่ต้องไป
    //   ปล่อยให้ผู้ใช้อยู่หน้า 3B เพื่อแก้เงื่อนไขแล้วลองใหม่ (ยกเลิก → ขั้นที่ 1 ดู FS-136 ด้านล่าง)
    //   FS-033 — งาน Export Excel ใช้ popup ตัวเดียวกัน แต่ต้องไม่พาไปหน้าขั้นที่ 6
    //   FS-031 — กด "ยกเลิกและลบไฟล์ที่สร้างแล้ว" แล้วเอกสารถูกลบหมด ก็ต้องไม่ไปเช่นกัน
    //            (popup ส่ง rolledBack = true มาให้ และตั้ง createdCount = 0 ไว้แล้ว)
    onBatchClosed: function(summary) {
      this.isSaving = false;
      this.isExporting = false;

      if (summary && summary.jobKind === "file") return;

      // FS-136 — งานบันทึกจบแบบ "ยกเลิก" ทุกทาง (กลางคัน · ก่อนใบแรกเสร็จ · FS-031 ลบหลังติดขัด)
      //   → กลับขั้นที่ 1 (เลือก Filter) โดยคงค่าที่เลือกไว้ (หน้าเลือก Filter เป็น parent route จึงไม่ถูกสร้างใหม่)
      //   เดิมยกเลิกกลางคันแล้ว popup ส่ง createdCount ของเอกสารที่เพิ่งถูกลบไป (H3)
      //   จึงพาไปหน้าสรุปผล แล้วขึ้น "ไม่พบเอกสารในชุดนี้"
      if (summary && (summary.state === "Cancelled" || summary.rolledBack)) {
        router.push({ path: "/PrintSummary/Barcodes/New" });
        return;
      }

      if (summary && summary.createdCount > 0 && summary.batchId) {
        // ส่ง batchId ทาง route params ไม่ใส่ใน URL
        //   spec: หน้าขั้นที่ 6 แสดงครั้งเดียว เปิดซ้ำไม่ได้ (ผู้ใช้ตัดสิน 25/09/2026)
        //   params ของ named route อยู่แค่ในหน่วยความจำ refresh แล้วหายตามที่ต้องการ
        router.push({
          name: "FormSummaryStockGroupResult",
          params: { batchId: summary.batchId },
        });
      }
    },
  },
  mounted() {
    this.loadDefaultSignature();
    this.loadBranchLine();
    // เข้าหน้านี้ตรง ๆ โดยไม่ผ่านขั้นที่ 1 (refresh / paste URL) → ไม่มีผลประมวลผลให้แสดง
    if (!this.groupResult) {
      router.replace({ path: "/PrintSummary/Barcodes/New" });
    }
  },
};
</script>

<style lang="scss" scoped>

/* FS-055 — ช่องค้นหาตัวแทนจำหน่ายเหนือตาราง (กว้างเต็มตารางตาม FS-053 ข้อ 2) */
.fss-preview__search {
  margin-bottom: 0.75rem;
}

.fss-preview__search-count {
  margin-top: 0.35rem;
  color: #74788d;
  font-size: 0.9rem;
}

/*
  FS-045 — Metronic ตั้ง .alert เป็น display:flex (flex-wrap: nowrap)
  ลูกทุกตัวจึงกลายเป็น flex item แยกกัน ทั้ง <i>, <strong> และ text node
  ผลคือ <strong> ถูกบีบตามพื้นที่ที่เหลือ (วัดได้ 33.8px) แล้วตัดคำกลางคำ
  "ข้อสำคัญ" กลายเป็น "ข้อ" บรรทัดบน "สำคัญ" บรรทัดล่าง ทับกับข้อความถัดไป
  และช่องไฟระหว่างก้อนก็หายเพราะ flex ไม่มี white-space ระหว่าง item

  คืนให้ไหลเป็นข้อความปกติ — ไอคอนเป็น inline นำหน้าเหมือนที่ design ตั้งใจ
*/
.alert {
  display: block;
}

.alert > i {
  margin-right: 0.35rem;
}

/* FS-017 — thumbnail ลายเซ็นข้างปุ่ม (ตาม design) ภาพมาจาก signed URL ของ endpoint Signature
   ห้ามชี้ไปที่ storage.googleapis.com ตรง ๆ — bucket ปิด เบราว์เซอร์จะได้ 403 (เหตุผลเดียวกับ FS-015) */
/* FS-061 — ปุ่มลายเซ็นเป็น flex ตาม mockup (gap 8px) เพื่อให้ thumbnail + ป้าย scope อยู่ในปุ่ม */
/* FS-067 — ปุ่มลายเซ็น + ปุ่มบันทึก ต้องอยู่บรรทัดเดียวกันทุกระดับซูม
   inline-flex เพื่อให้กล่องยังถูกจัดชิดขวาโดย .text-right ของคอลัมน์
   nowrap คือหัวใจ — ห้ามตัดบรรทัดไม่ว่าความกว้างคอลัมน์จะเหลือเท่าไร
   ระยะห่างยังใช้ kt-margin-r-10 เดิมของปุ่ม จึงไม่ต้องใส่ gap ให้ซ้อนกัน */
.fss-action-group {
  display: inline-flex;
  flex-wrap: nowrap;
  /* FS-073 — stretch ให้ปุ่มบันทึกสูงเท่าปุ่มลายเซ็น (ซึ่งสูงกว่าเพราะมี thumbnail 28px อยู่ในปุ่ม)
     ข้อความใน <button> อยู่กึ่งกลางแนวตั้งเองอยู่แล้ว · ไม่ตั้งความสูงตายตัว thumbnail เปลี่ยนขนาดก็ตามไปเอง */
  align-items: stretch;
  justify-content: flex-end;
  max-width: 100%;
}

/* FS-119 — ปุ่มไม่หด และข้อความในปุ่มไม่ตัดบรรทัด (กันซ้ำถ้าคอลัมน์แคบกว่าที่คาด) */
.fss-action-group > .btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.fss-signature-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.fss-signature-thumb {
  height: 24px;
  max-width: 96px;
  object-fit: contain;
  /* พื้นขาวบังคับ — อยู่ในปุ่มเขียวแล้ว ลายเซ็นสีเข้มจะจมถ้าไม่มีพื้น */
  background: #fff;
  /* FS-061 — กรอบเขียวเข้มตาม mockup (#145a34) ให้เข้ากับปุ่ม */
  border: 1px solid #145a34;
  border-radius: 3px;
  padding: 1px 3px;
  vertical-align: middle;
}

/* FS-036 ข้อ 7 — ป้าย "ใช้กับทั้ง N เอกสาร" ข้างปุ่มลายเซ็น (ตาม mockup ขั้นที่ 3B) */
/* FS-061 — ป้ายอยู่ในปุ่มเขียวแล้ว สีเทาเดิมอ่านไม่ออก เปลี่ยนเป็นโปร่งบนพื้นปุ่ม */
.fss-signature-scope {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  background-color: rgba(255, 255, 255, 0.22);
  color: inherit;
  font-size: 0.8rem;
  white-space: nowrap;
  vertical-align: middle;
}

/* stepper / filter box / total row — คัดลอกจาก FormSummaryStockPreview.vue เพื่อให้ 2 หน้าหน้าตาเหมือนกัน */
.fss-filter-text {
  padding: 0.5rem 0.75rem;
  background-color: #f7f8fa;
  border-radius: 4px;
}

.fss-filter-table {
  margin-bottom: 0;

  &__label {
    width: 12rem;
    font-weight: 600;
    color: #6c7293;
  }
}

.fss-total-row {
  background-color: #f7f8fa;
}

/* FS-106 — บรรทัดสาขาใต้ชื่อตัวแทน : ค่าเดียวกับหน้าตัวแทนรายเดียว (FS-074) */
.fss-branch-line {
  margin-top: 0.25rem;
  color: #74788d;
  font-size: 0.85rem;
  font-weight: normal;
}

/* กล่อง "Export Excel ตรวจรายละเอียด" */
/* FS-068 — ปุ่มอยู่บน คำเตือนอยู่ใต้ปุ่ม ทั้งคู่ชิดขวา */
.fss-export-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #f7f8fa;
  border-radius: 4px;

  &__note {
    margin-top: 0.4rem;
    text-align: right;
  }
}

/* แถวรายละเอียดที่กางออกมา */
.fss-detail-row > td {
  background-color: #fafbfc;
}

.fss-detail {
  padding: 0.5rem 0.25rem;

  &__title {
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #6c7293;
  }
}

/* เส้นคั่น + กลุ่มตัวแทนที่ไม่มีข้อมูล */
.fss-skip-sep > td {
  background-color: #f7f8fa;
  font-weight: 600;
  color: #6c7293;
}

.fss-skip-row {
  color: #a2a5b9;
}

/* FS-105 — รายที่ติดขัด : หน้าตาเดียวกับ "ไม่มีข้อมูลคงค้าง" ต่างแค่ข้อความ (โทนแดงอ่อนให้แยกออก) */
.fss-failed-sep > td {
  color: #c62828;
}

.fss-failed-note {
  color: #c62828;
}

.fss-failed-ref {
  color: #a2a5b9;
  font-size: 0.85rem;
  margin-left: 0.25rem;
}
</style>



// WEBPACK FOOTER //
// App/Components/PrintSummary/FormSummaryStockGroupPreview.vue